/**
 * ZoomLevelSelector - Input + dropdown combo for precise zoom level selection
 *
 * Updated in Feature 646 to support free zoom from trackpad gestures.
 * Now uses ZoomComboControl which supports:
 * - Typing arbitrary zoom values (e.g., "137")
 * - Typing "fit" for fit-to-viewport mode
 * - Selecting from preset zoom levels in dropdown
 * - Displays current zoom (updated in real-time during trackpad gestures)
 *
 * Features:
 * - Text input for typing zoom values
 * - Dropdown for preset selection
 * - Real-time updates from trackpad pinch-zoom
 * - Keyboard shortcuts (Enter to apply, Escape to cancel)
 */

import { ZoomComboControl } from '@/components/composed/ZoomComboControl';
import type { ZoomLevel } from '@/hooks/useZoomPan';

export interface ZoomLevelSelectorProps {
  /** Current zoom level (displayed in input, can be any number or 'fit') */
  currentZoom: ZoomLevel;

  /** Callback when zoom level changes */
  onChange: (level: ZoomLevel) => void;

  /** Optional CSS class for the control */
  className?: string;
}

/**
 * ZoomLevelSelector - Wrapper around ZoomComboControl
 * Maintains backwards compatibility while using new combo control
 */
export function ZoomLevelSelector({
  currentZoom,
  onChange,
  className = '',
}: ZoomLevelSelectorProps) {
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Zoom Icon (magnifying glass) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4 text-gray-100"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
          clipRule="evenodd"
        />
      </svg>

      {/* Combo Control (input + dropdown) */}
      <ZoomComboControl
        currentZoom={currentZoom}
        onZoomChange={onChange}
      />
    </div>
  );
}
